import { extendTheme } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';

// Modern color palette - clean and professional
const colors = {
  // Primary - Deep Ocean Blue
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe',
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#0ea5e9', // Main brand color
    600: '#0284c7',
    700: '#0369a1',
    800: '#075985',
    900: '#0c4a6e',
  },
  
  // Secondary - Rich Purple
  secondary: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7',
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
  },
  
  // Accent - Vibrant Emerald
  accent: {
    50: '#ecfdf5',
    100: '#d1fae5',
    200: '#a7f3d0',
    300: '#6ee7b7',
    400: '#34d399',
    500: '#10b981',
    600: '#059669',
    700: '#047857',
    800: '#065f46',
    900: '#064e3b',
  },
  
  // Status colors - clean and clear
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },
  
  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },
  
  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },
};

// Typography - professional fonts
const fonts = {
  heading: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
  body: `'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif`,
  mono: `'JetBrains Mono', 'Fira Code', Consolas, 'Liberation Mono', Menlo, Courier, monospace`,
};

// Component styles - clean and modern
const components = {
  Button: {
    baseStyle: {
      fontWeight: '500',
      borderRadius: 'lg',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    variants: {
      solid: {
        _hover: {
          transform: 'translateY(-1px)',
          boxShadow: 'lg',
        },
        _active: {
          transform: 'translateY(0)',
        },
      },
      outline: {
        borderWidth: '1.5px',
        _hover: {
          transform: 'translateY(-1px)',
          boxShadow: 'md',
        },
      },
    },
  },
  
  Card: {
    baseStyle: {
      container: {
        borderRadius: 'xl',
        boxShadow: 'sm',
        border: '1px solid',
        borderColor: 'gray.200',
        bg: 'white',
      },
    },
  },
  
  Input: {
    variants: {
      outline: {
        field: {
          borderRadius: 'lg',
          borderWidth: '1.5px',
          _focus: {
            borderColor: 'primary.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-primary-500)',
          },
        },
      },
    },
  },
};

// Global styles with color mode awareness
const styles = {
  global: (props) => ({
    body: {
      bg: mode('gray.50', 'gray.900')(props),
      color: mode('gray.800', 'gray.100')(props),
      fontFamily: 'body',
      lineHeight: 'base',
    },
    '*::placeholder': {
      color: mode('gray.400', 'gray.500')(props),
    },
    '*, *::before, &::after': {
      borderColor: mode('gray.200', 'gray.700')(props),
      wordWrap: 'break-word',
    },
  }),
};

// Configuration
const config = {
  initialColorMode: 'light',
  useSystemColorMode: true,
  cssVarPrefix: 'chakra',
  disableTransitionOnChange: false,
};

// Create clean theme without dark mode
const theme = extendTheme({
  colors,
  fonts,
  components,
  styles,
  config,
  space: {
    '4.5': '1.125rem',
    '5.5': '1.375rem',
    '6.5': '1.625rem',
    '7.5': '1.875rem',
  },
  sizes: {
    '4.5': '1.125rem',
    '5.5': '1.375rem', 
    '6.5': '1.625rem',
    '7.5': '1.875rem',
  },
  radii: {
    'xl': '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
  },
  shadows: {
    'outline': '0 0 0 3px rgba(14, 165, 233, 0.1)',
    'outline-error': '0 0 0 3px rgba(239, 68, 68, 0.1)',
    'outline-success': '0 0 0 3px rgba(34, 197, 94, 0.1)',
  },
});

export default theme;